/* Tradeline cart: quantity changes, removals and order notes for the cart drawer and cart page.
 * All listeners are delegated on document, so markup re-rendered by Tradeline.cart.refresh()
 * (Section Rendering API) keeps working without re-binding. */
(() => {
  if (window.__tlCartLoaded) return;
  window.__tlCartLoaded = true;

  const T = window.Tradeline || {};
  const timers = new Map(); // line index -> debounce timer
  const inflight = new Set();
  let focusMemo = null;
  let noteOpen = null;

  const status = () => Array.from(document.querySelectorAll('[data-cart-status]'));
  const msg = (key) => { const el = status()[0]; return (el && el.dataset[key]) || ''; };
  const announce = (text) => {
    if (!text) return;
    status().forEach((el) => { el.textContent = ''; setTimeout(() => { el.textContent = text; }, 60); });
  };

  /* ---------- Focus memory across re-renders ---------- */
  const rememberFocus = () => {
    const a = document.activeElement;
    const section = a && a.closest('[data-cart-section]');
    focusMemo = section ? { section: section.dataset.cartSection, key: a.dataset.focusKey || null } : null;
  };
  const restoreFocus = () => {
    const memo = focusMemo;
    focusMemo = null;
    if (!memo) return;
    const root = document.querySelector(`[data-cart-section="${memo.section}"]`);
    if (!root) return;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected && root.contains(active)) return;
    const esc = (k) => (window.CSS && CSS.escape ? CSS.escape(k) : k);
    const target = (memo.key && root.querySelector(`[data-focus-key="${esc(memo.key)}"]`))
      || root.querySelector('.tl-line .tl-qty input')
      || root.querySelector('[data-focus-key]');
    if (target) target.focus({ preventScroll: true });
  };

  /* ---------- Line updates ---------- */
  const lineEl = (el) => el.closest('[data-cart-line]');
  const setBusy = (li, busy) => {
    if (!li) return;
    li.classList.toggle('is-updating', busy);
    li.setAttribute('aria-busy', String(busy));
    li.querySelectorAll('button, input').forEach((c) => { c.disabled = busy; });
    li.querySelectorAll('[data-line-remove]').forEach((a) => a.setAttribute('aria-disabled', String(busy)));
  };

  const changeLine = async (line, quantity, li) => {
    rememberFocus();
    setBusy(li, true);
    const job = (async () => {
      try {
        await T.cart.change(Number(line), quantity, 'cart');
        announce(quantity === 0 ? msg('msgRemoved') : msg('msgUpdated'));
        // Re-rendered markup replaced the line; put focus back where it was.
        if (focusMemo) restoreFocus();
      } catch (err) {
        const text = (err && err.message) || msg('msgError');
        announce(text);
        if (T.toast) T.toast(text);
        if (li && li.isConnected) {
          const input = li.querySelector('input[data-line]');
          if (input) input.value = input.dataset.value;
        }
      } finally {
        if (li && li.isConnected) setBusy(li, false);
        if (focusMemo) restoreFocus();
      }
    })();
    inflight.add(job);
    job.finally(() => inflight.delete(job));
    return job;
  };

  const schedule = (input, delay = 400) => {
    const line = input.dataset.line;
    clearTimeout(timers.get(line));
    timers.set(line, setTimeout(() => {
      timers.delete(line);
      const raw = parseInt(input.value, 10);
      const qty = Number.isFinite(raw) && raw > 0 ? raw : 0;
      if (String(qty) === input.dataset.value) { input.value = qty; return; }
      changeLine(line, qty, lineEl(input));
    }, delay));
  };

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-section] input[data-line]');
    if (input) schedule(input);
  });

  document.addEventListener('keydown', (e) => {
    const input = e.target.closest('[data-cart-section] input[data-line]');
    if (input && e.key === 'Enter') { e.preventDefault(); schedule(input, 0); }
  });

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-section] [data-line-remove]');
    if (!remove) return;
    e.preventDefault();
    if (remove.getAttribute('aria-disabled') === 'true') return;
    const li = lineEl(remove);
    // Focus a stable control first so focus can be restored after the line disappears.
    remove.focus();
    clearTimeout(timers.get(remove.dataset.lineRemove));
    changeLine(remove.dataset.lineRemove, 0, li);
  });

  /* ---------- Checkout waits for pending quantity changes ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[name="checkout"]');
    if (!btn || (!timers.size && !inflight.size)) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    document.querySelectorAll('[data-cart-section] input[data-line]').forEach((input) => {
      if (timers.has(input.dataset.line)) {
        clearTimeout(timers.get(input.dataset.line));
        timers.delete(input.dataset.line);
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        if (String(qty) !== input.dataset.value) changeLine(input.dataset.line, qty, lineEl(input));
      }
    });
    await Promise.allSettled(Array.from(inflight));
    btn.classList.remove('is-loading');
    const form = document.getElementById(btn.getAttribute('form')) || btn.form;
    const fresh = form && document.querySelector(`button[name="checkout"][form="${form.id}"]`);
    if (form && form.requestSubmit) form.requestSubmit(fresh || undefined);
    else if (form) form.submit();
  });

  /* ---------- Order note ---------- */
  let noteTimer;
  const saveNote = async (value) => {
    try {
      // Posted directly (not via Tradeline.cart.update) so the section isn't re-rendered while typing.
      const res = await fetch(`${T.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value })
      });
      if (!res.ok) throw new Error(msg('msgError'));
    } catch (err) {
      announce((err && err.message) || msg('msgError'));
    }
  };
  document.addEventListener('input', (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    document.querySelectorAll('[data-cart-note]').forEach((n) => { if (n !== note) n.value = note.value; });
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => saveNote(note.value), 600);
  });
  document.addEventListener('toggle', (e) => {
    if (e.target.matches && e.target.matches('[data-cart-note-details]')) noteOpen = e.target.open;
  }, true);

  /* ---------- Gift wrapping ---------- */
  let giftTimer;
  const saveGiftMessage = (key, value) => fetch(`${T.routes.cartUpdate}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [key]: value } })
  });
  document.addEventListener('change', async (e) => {
    const box = e.target.closest('[data-gift-wrap]');
    if (!box) return;
    rememberFocus();
    box.disabled = true;
    try {
      const line = Number(box.dataset.wrapLine || 0);
      if (box.checked && !line) await T.cart.add({ id: Number(box.dataset.variantId), quantity: 1 }, 'gift-wrap');
      else if (!box.checked && line) {
        const msgEl = document.querySelector('[data-gift-message]');
        if (msgEl && msgEl.value) await saveGiftMessage(msgEl.dataset.giftMessage, '');
        await T.cart.change(line, 0, 'gift-wrap');
      }
      announce(msg('msgUpdated'));
      restoreFocus();
    } catch (err) {
      box.checked = !box.checked;
      announce((err && err.message) || msg('msgError'));
    } finally {
      box.disabled = false;
    }
  });
  document.addEventListener('input', (e) => {
    const el = e.target.closest('[data-gift-message]');
    if (!el) return;
    document.querySelectorAll('[data-gift-message]').forEach((n) => { if (n !== el) n.value = el.value; });
    clearTimeout(giftTimer);
    giftTimer = setTimeout(() => saveGiftMessage(el.dataset.giftMessage, el.value).catch(() => announce(msg('msgError'))), 600);
  });

  /* ---------- After any cart update ---------- */
  document.addEventListener('tl:cart:updated', () => {
    if (noteOpen !== null) document.querySelectorAll('[data-cart-note-details]').forEach((d) => { d.open = noteOpen; });
  });

  /* ---------- Cart drawer focus trap ---------- */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const drawer = document.querySelector('#CartDrawer.is-open');
    if (!drawer) return;
    const items = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])'))
      .filter((el) => el.getClientRects().length);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!drawer.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------- Restore cart state when returning via back/forward cache ---------- */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted && T.cart && document.querySelector('[data-cart-section]')) {
      T.fetchSections(T.cartSections(), window.location.pathname)
        .then((sections) => T.cart.refresh(sections, 'pageshow'))
        .catch(() => {});
    }
  });
})();
